import { MaterialSymbol } from '@florencelabs/icons';
import { ModalCloseButton, DialogTitle } from '@florencelabs/modal-2';
import Link from 'next/link';
import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { DecisionTreeErrorSceneProps } from '@provider-directory/components/modals/decision-tree-error-scene-props.types';
import {
  useDecisionTree,
  getSchedulingFlowType,
} from '@provider-directory/contexts/DecisionTreeContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { getProviderPrimaryPhoneNumber } from '@provider-directory/functions/get-available-phone';
import { useDecisionTreeConversionClick } from '@provider-directory/hooks/useDecisionTreeConversionClick';
import { useAnalytics } from '../analytics/analytics-provider';
import { ContactsList, NoBreak, Phone } from '../helpers';
import {
  useOrgContext,
  useOrgContextBasedOnHospitalAssociations,
} from '../org-context/org-context-provider';
import { DecisionTreeErrorButton } from './decision-tree-error-button';

export const DecisionTreeError = ({
  name,
  icon,
  code,
  title,
  body,
  specialty = '',
  ctaButtons = [],
}: DecisionTreeErrorSceneProps): ReactElement => {
  const { provider } = useProvider();
  const {
    key,
    name: healthSystemName,
    decisionTree: decisionTreeConfig,
  } = useOrgContextBasedOnHospitalAssociations(provider?.hospitalAssociations);
  const orgContext = useOrgContext();
  const { t } = useTranslation(key);
  const { decisionTree } = useDecisionTree();
  const onConversionClick = useDecisionTreeConversionClick();
  const analytics = useAnalytics();

  // Resolved from the ACTIVE directory (not the association-resolved sibling
  // above), consistent with how the MyChart scheduling URL is resolved.
  const myChartLoginUrl = orgContext.getMyChartLoginUrl(provider ?? undefined);

  // if a phone override exists on the config, use it
  const configPhone =
    decisionTreeConfig.scenesConfig[name]?.scenePhoneNumber ??
    decisionTreeConfig.mainPhoneNumber;

  const contactList =
    decisionTreeConfig.scenesConfig[name]?.contacts ??
    decisionTreeConfig.contacts ??
    [];

  const appointmentName = decisionTree.rfvKey
    ? t(`decisionTree.reasonForVisit.${decisionTree.rfvKey}.label`)
    : null;

  return (
    <div
      className={
        'mx-auto flex w-full flex-col rounded-b-lg px-6 pb-16 pt-10 tablet:px-16 desktop:px-12'
      }
    >
      <div className={'flex flex-row justify-end'}>
        <ModalCloseButton className={'static'} ariaLabel={t('close')} />
      </div>
      {icon && (
        <div className={'flex w-full pb-6'}>
          <MaterialSymbol
            size={40}
            name={icon}
            className={'text-primary-950'}
          />
        </div>
      )}
      <div className={'flex w-full flex-col gap-3 pb-10'}>
        {code && <p className={'text-body-small-bold'}>{code.toUpperCase()}</p>}
        <DialogTitle
          className={'font-serif text-display-xlarge'}
          data-testid={'error-title-modal'}
        >
          {
            <Trans
              t={t}
              i18nKey={title}
              specialty={specialty}
              values={{
                specialty: t(specialty),
                healthSystem: healthSystemName,
                appointmentName,
                providerName: provider?.name.full,
              }}
              components={{
                nobreak: <NoBreak />,
              }}
            />
          }
        </DialogTitle>
        <div>
          <Trans
            t={t}
            i18nKey={body}
            count={contactList.length}
            specialty={specialty}
            values={{
              specialty: t(specialty),
              healthSystem: healthSystemName,
              appointmentName,
              providerName: provider?.name.full,
            }}
            components={{
              myChartLink: myChartLoginUrl ? (
                <Link
                  onClick={() => {
                    analytics.ehrRedirectEvent({
                      linkContextPage: 'ProviderDetails',
                      linkContextComponent: 'DecisionTree',
                      provider: provider ?? undefined,
                      phoneNumber: getProviderPrimaryPhoneNumber(
                        provider,
                        orgContext.getAssociationPhoneSelection(),
                      ),
                      schedulingFlowType: getSchedulingFlowType(),
                      redirectUrl: myChartLoginUrl,
                    });
                  }}
                  className={'text-primary-color underline'}
                  href={myChartLoginUrl}
                  target={'_blank'}
                />
              ) : (
                // MyChart is not configured for this directory: keep the copy but
                // render it inert instead of a dead link.
                <span />
              ),
              nobreak: <NoBreak />,
              phone: <Phone onClick={onConversionClick} phone={configPhone} />,
              contactsList: (
                <ContactsList
                  onClick={onConversionClick}
                  contacts={contactList}
                />
              ),
            }}
          />
        </div>
      </div>
      <div className={'flex flex-col gap-2 tablet:flex-row'}>
        {ctaButtons.map((button, index) => (
          <DecisionTreeErrorButton
            type={button}
            variant={index === 0 ? 'clientMain' : 'systemOutline'}
            key={button}
          />
        ))}
      </div>
    </div>
  );
};
